import { AdminProjects } from '@platform/client';
import { Icon, PillBadge, PillBadgeColors } from '@platform/components';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import cx from 'classix';
import { AdminProject } from 'lib/client/src/platformapi.schemas';
import { useMemo, useState, useContext } from 'react';
import ProjectPublishStageModal from './ProjectPublishStageModal';
import ProjectRecommendationModal from './ProjectRecommendationModal';
import { getDaysSince } from '../../../util/DateHelpers';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import {
  AdminActionsButtonProps,
  AdminActionsContainer,
  AdminListPageWrapper,
} from '../../AdminListPage';
import { OrganizationProfileLink, ProjectLink } from '../../components/ProfileLinks';

const columnHelper = createColumnHelper<AdminProject>();

export const ProjectsAdmin = () => {
  return (
    <AdminListPageWrapper title={'Projects'}>
      <ProjectsAdminContent />
    </AdminListPageWrapper>
  );
};

const ProjectsAdminContent = () => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [publishStageModalIsOpen, setpublishStageModalIsOpen] = useState<boolean>(false);
  const [publishStageModalProject, setPublishStageModalProject] = useState<
    AdminProject | undefined
  >();
  const [recommendProjectId, setRecommendProjectId] = useState<number | undefined>();

  const {
    data: projectsResponse,
    isError,
    isLoading,
    isSuccess,
  } = AdminProjects.useGetProjects(
    {
      ...adminQueryOptionsV2,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const handlePublishStageEditClick = (row: AdminProject) => {
    setPublishStageModalProject(row);
    setpublishStageModalIsOpen(true);
  };

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          cell: (id) => id.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>ID</span>,
          size: 25,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('name', {
          cell: ({ row }) => {
            return <ProjectLink projectId={row.original.id} projectName={row.original.name} />;
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Name</span>,
          size: 150,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('organizationName', {
          cell: ({ row }) => {
            return (
              <OrganizationProfileLink
                organizationId={row.original.organizationId}
                organizationName={row.original.organizationName}
              />
            );
          },
          header: () => (
            <span className='overflow-ellipsis whitespace-nowrap'>Organization Name</span>
          ),
          size: 150,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('representativeName', {
          cell: (repName) => repName.getValue(),
          header: () => (
            <span className='overflow-ellipsis whitespace-nowrap'>Representative Name</span>
          ),
          size: 100,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('skills', {
          cell: (skills) => {
            return skills.getValue().map((skill: string) => (
              <PillBadge variant='plain' color='indigo' key={skill} size='sm'>
                {skill}
              </PillBadge>
            ));
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Skills</span>,
          size: 100,
          enableSorting: false,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('openToTeam', {
          cell: (openToTeam) => (openToTeam.getValue() ? 'Yes' : 'No'),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Teams</span>,
          size: 50,
          enableSorting: false,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('matchNetworks', {
          cell: (matchNetworks) => {
            return matchNetworks.getValue().map((network: string) => (
              <PillBadge variant='plain' color='blue' key={network} size='sm'>
                {network}
              </PillBadge>
            ));
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Match Networks</span>,
          size: 25,
          enableSorting: false,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('publishingStage', {
          cell: ({ row, cell }) => {
            const stage = cell.getValue();
            const daysSinceUpdate = getDaysSince(row.original.publishingStageUpdatedDate);
            const daysSinceUpdateText =
              daysSinceUpdate === 0 ? 'today' : (
                `${daysSinceUpdate} day${daysSinceUpdate > 1 ? 's' : ''}`
              );
            const badgeColor = getProjectBadgeColor(stage, row.original.publishingStageUpdatedDate);
            return (
              <div className='flex justify-between align-middle'>
                <PillBadge variant='dot' size='md' color={badgeColor as PillBadgeColors}>
                  {stage}
                </PillBadge>
                {stage === 'review' && (
                  <p
                    className={cx(
                      stage === 'review' && daysSinceUpdate >= 6 && 'font-bold text-error-600',
                    )}
                  >
                    ({daysSinceUpdateText})
                  </p>
                )}
                <button onClick={() => handlePublishStageEditClick(row.original)}>
                  <Icon type='edit-01' size='14' />
                </button>
              </div>
            );
          },
          header: () => (
            <span className='overflow-ellipsis whitespace-nowrap'>Publishing Stage</span>
          ),
          size: 25,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('stage', {
          cell: (stage) => stage.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Match Stage</span>,
          size: 25,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('createdAt', {
          cell: (createdAt) => new Date(createdAt.getValue()).toLocaleDateString(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Created</span>,
          size: 25,
          meta: {
            filterVariant: 'date-select',
          },
        }),
        columnHelper.display({
          id: 'actions',
          cell: ({ row }) => {
            return (
              <ProjectActionsCell
                projectId={row.original.id}
                setRecommendProjectId={
                  row.original.publishingStage === 'published' ? setRecommendProjectId : undefined
                }
              />
            );
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Actions</span>,
          size: 50,
        }),
      ] as ColumnDef<AdminProject>[],
    [],
  );

  return (
    <>
      <AdminTable
        data={projectsResponse?.projects ?? []}
        columns={columns}
        filteredCount={projectsResponse?.filteredCount ?? 0}
        isLoading={isLoading}
        isError={isError}
        isSuccess={isSuccess}
        enableFiltering={true}
      />
      <ProjectPublishStageModal
        project={publishStageModalProject as AdminProject}
        isOpen={publishStageModalIsOpen}
        onClose={() => {
          setpublishStageModalIsOpen(false);
        }}
      />
      {recommendProjectId && (
        <ProjectRecommendationModal
          projectId={recommendProjectId}
          onClose={() => setRecommendProjectId(undefined)}
        />
      )}
    </>
  );
};

const ProjectActionsCell = (props: {
  projectId: number;
  setRecommendProjectId?: (id: number) => void;
}) => {
  const { setRecommendProjectId, projectId } = props;
  const actions: AdminActionsButtonProps[] = [
    { text: 'Review', type: 'link', href: `/review/${projectId}` },
    {
      text: 'Edit',
      type: 'link',
      href: `${import.meta.env['VITE_LEGACY_APP_URL']}/setup/opportunity/${projectId}/details`,
    },
  ];
  if (setRecommendProjectId) {
    actions.push({
      text: 'Recommend',
      type: 'button',
      onPress: () => setRecommendProjectId(projectId),
    });
  }
  return <AdminActionsContainer actions={actions} />;
};

export const getProjectBadgeColor = (
  stage: string,
  publishStageUpdatedString: string,
): PillBadgeColors => {
  const daysSinceUpdate = getDaysSince(publishStageUpdatedString);
  let badgeColor: PillBadgeColors = 'gray';
  if (stage === 'published') {
    badgeColor = 'success-500';
  } else if (stage === 'review' || stage === 'revision_requested') {
    badgeColor = stage === 'review' && daysSinceUpdate >= 6 ? 'error' : 'warning';
  }
  return badgeColor;
};
